import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState, type FormEvent } from "react";
import { tvApi, type TvUnmatchedFile } from "@/api/tv";
import { Button } from "@/components/ui/Button";
import { Field, NumberInput, Select, TextInput } from "@/components/ui/Field";
import { Modal } from "@/components/ui/Modal";
import { DownloadsEnabledCard } from "@/components/DownloadsEnabledCard";
import { PageHeader } from "@/components/ui/PageHeader";
import { Skeleton } from "@/components/ui/Skeleton";
import { Status } from "@/components/ui/Status";
import { useToast } from "@/components/ui/Toast";

const INTERVAL_PRESETS: { value: number; label: string }[] = [
  { value: 0, label: "Manual only" },
  { value: 1, label: "Every hour" },
  { value: 6, label: "Every 6 hours" },
  { value: 12, label: "Every 12 hours" },
  { value: 24, label: "Daily" },
  { value: 168, label: "Weekly" },
];

export function TVSettings() {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [libraryPath, setLibraryPath] = useState("");
  const [scanInterval, setScanInterval] = useState(0);
  const [refreshInterval, setRefreshInterval] = useState(0);
  // Issue #64: per-episode air-date gate. Default `0` = search at air
  // time. Negative values open up pre-air leak hunting; positive values
  // wait N hours after air for encodes to settle.
  const [episodeLeadHours, setEpisodeLeadHours] = useState(0);
  // Issue #66: global default for the per-series "monitor specials"
  // toggle. `false` matches Sonarr's default and avoids cluttering the
  // library with a 30-episode pile of OVAs / pilots / behind-the-scenes
  // shorts unless the user opts in.
  const [monitorSpecialsDefault, setMonitorSpecialsDefault] = useState(false);

  const { data: settings, isLoading } = useQuery({
    queryKey: ["settings", "tv"],
    queryFn: tvApi.settings.get,
  });
  const { data: status } = useQuery({
    queryKey: ["tv", "scan", "status"],
    queryFn: tvApi.scan.status,
    refetchInterval: (q) => (q.state.data?.running ? 3000 : 30000),
  });

  useEffect(() => {
    if (settings) {
      setLibraryPath(settings.library_path ?? "");
      setScanInterval(settings.scan_interval_hours);
      setRefreshInterval(settings.refresh_interval_hours);
      setEpisodeLeadHours(settings.episode_grab_lead_hours);
      setMonitorSpecialsDefault(settings.monitor_specials_default);
    }
  }, [
    settings?.library_path,
    settings?.scan_interval_hours,
    settings?.refresh_interval_hours,
    settings?.episode_grab_lead_hours,
    settings?.monitor_specials_default,
  ]);

  const saveMut = useMutation({
    mutationFn: (body: {
      library_path?: string;
      scan_interval_hours?: number;
      refresh_interval_hours?: number;
      episode_grab_lead_hours?: number;
      monitor_specials_default?: boolean;
    }) => tvApi.settings.update(body),
    onSuccess: () => {
      toast("TV settings saved", "success");
      qc.invalidateQueries({ queryKey: ["settings", "tv"] });
      qc.invalidateQueries({ queryKey: ["tv", "scan", "status"] });
      // Issue #66: bump series + wanted caches so a flipped global
      // default propagates to series with no per-series override.
      qc.invalidateQueries({ queryKey: ["series"] });
      qc.invalidateQueries({ queryKey: ["tv", "wanted"] });
    },
    onError: (e) =>
      toast(e instanceof Error ? e.message : "Save failed", "error"),
  });

  const scanMut = useMutation({
    mutationFn: () => tvApi.scan.start(),
    onSuccess: () => {
      toast("Scan started", "info");
      qc.invalidateQueries({ queryKey: ["tv", "scan", "status"] });
    },
    onError: (e) =>
      toast(e instanceof Error ? e.message : "Scan failed to start", "error"),
  });

  const onSave = (e: FormEvent) => {
    e.preventDefault();
    saveMut.mutate({
      library_path: libraryPath.trim(),
      scan_interval_hours: scanInterval,
      refresh_interval_hours: refreshInterval,
      episode_grab_lead_hours: episodeLeadHours,
      monitor_specials_default: monitorSpecialsDefault,
    });
  };

  return (
    <div className="space-y-8">
      <PageHeader
        eyebrow="Settings"
        title="TV"
        description="Library path, scan schedule, and TMDB metadata refresh schedule. The scanner walks the library, parses series + SxxEyy from filenames, and links existing episode files to monitored series."
      />

      <DownloadsEnabledCard kind="tv" />

      <ScanStatusCard
        loading={!status}
        running={status?.running ?? false}
        lastScanAt={status?.last_scan_at ?? null}
        unmatchedCount={status?.unmatched_count ?? 0}
        onScanNow={() => scanMut.mutate()}
        scanning={scanMut.isPending}
      />

      <form onSubmit={onSave} className="space-y-5">
        <Field
          label="Library path"
          hint={
            settings?.library_path_source === "env"
              ? "Currently sourced from TV_LIBRARY_PATH env. Set a value here to override."
              : "Absolute path arr-in-one imports episodes into and scans for existing files."
          }
        >
          <TextInput
            value={libraryPath}
            placeholder="/tv"
            onChange={(e) => setLibraryPath(e.target.value)}
          />
        </Field>

        <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
          <Field
            label="Scan interval"
            hint="How often the background scanner walks the library and links existing files."
          >
            <Select
              value={String(scanInterval)}
              onChange={(e) => setScanInterval(Number(e.target.value))}
            >
              {INTERVAL_PRESETS.map((p) => (
                <option key={p.value} value={p.value}>
                  {p.label}
                </option>
              ))}
              {INTERVAL_PRESETS.every((p) => p.value !== scanInterval) && (
                <option value={scanInterval}>
                  Every {scanInterval} hours (custom)
                </option>
              )}
            </Select>
          </Field>

          <Field
            label="TMDB refresh interval"
            hint="How often we re-fetch metadata (status, posters, IMDB ids) for monitored series."
          >
            <Select
              value={String(refreshInterval)}
              onChange={(e) => setRefreshInterval(Number(e.target.value))}
            >
              {INTERVAL_PRESETS.map((p) => (
                <option key={p.value} value={p.value}>
                  {p.label}
                </option>
              ))}
              {INTERVAL_PRESETS.every((p) => p.value !== refreshInterval) && (
                <option value={refreshInterval}>
                  Every {refreshInterval} hours (custom)
                </option>
              )}
            </Select>
          </Field>
        </div>

        <Field
          label="Episode grab lead time"
          hint="Hours offset from each episode's air date at which auto-grab becomes allowed. 0 = search at air time (default). Positive values wait N hours after air for encodes to fully propagate. Negative values allow searches before air. Manual searches are unaffected."
        >
          <div className="flex items-center gap-2">
            <NumberInput
              value={String(episodeLeadHours)}
              onChange={(e) =>
                setEpisodeLeadHours(
                  Number.isFinite(Number(e.target.value))
                    ? Math.trunc(Number(e.target.value))
                    : 0,
                )
              }
              className="w-32"
            />
            <span className="font-mono text-[11px] text-foreground/55">
              hours{" "}
              <span className="text-foreground/35">(default 0)</span>
            </span>
          </div>
        </Field>

        <Field
          label="Monitor specials by default"
          hint="When on, newly-added series include S00 (specials, OVAs, behind-the-scenes shorts) in the Wanted list and auto-grab fan-out. Per-series toggle on each series's detail page overrides this."
        >
          <label className="flex cursor-pointer items-center gap-3">
            <input
              type="checkbox"
              checked={monitorSpecialsDefault}
              onChange={(e) => setMonitorSpecialsDefault(e.target.checked)}
              className="h-4 w-4 rounded border-line"
            />
            <span className="font-mono text-[11px] text-foreground/55">
              {monitorSpecialsDefault
                ? "Specials are eligible by default"
                : "Specials are excluded by default"}
            </span>
          </label>
        </Field>

        {settings?.last_refresh_at && (
          <p className="font-mono text-[11px] text-foreground/45">
            Last metadata refresh{" "}
            <span className="text-foreground/65">
              {new Date(settings.last_refresh_at).toLocaleString()}
            </span>
          </p>
        )}

        <div className="flex justify-end border-t border-line pt-4">
          <Button type="submit" loading={saveMut.isPending} disabled={isLoading}>
            Save changes
          </Button>
        </div>
      </form>

      <UnmatchedSection count={status?.unmatched_count ?? 0} />
    </div>
  );
}

function ScanStatusCard({
  loading,
  running,
  lastScanAt,
  unmatchedCount,
  onScanNow,
  scanning,
}: {
  loading: boolean;
  running: boolean;
  lastScanAt: string | null;
  unmatchedCount: number;
  onScanNow: () => void;
  scanning: boolean;
}) {
  return (
    <div className="rounded-md border border-line bg-surface/50 px-5 py-4">
      <div className="flex items-start justify-between gap-3">
        <div className="space-y-1.5">
          <div className="eyebrow">Scanner</div>
          <div className="text-sm">
            {loading ? (
              <Skeleton.Block height="1rem" width="14rem" />
            ) : running ? (
              <Status tone="info" pulse>
                Scan running
              </Status>
            ) : lastScanAt ? (
              <span className="text-foreground/85">
                Last scan{" "}
                <span className="font-mono text-foreground/55">
                  {new Date(lastScanAt).toLocaleString()}
                </span>
              </span>
            ) : (
              <span className="text-foreground/55">
                Never scanned. Configure the library path below and run a scan.
              </span>
            )}
          </div>
          {unmatchedCount > 0 && (
            <p className="font-mono text-[11px] text-foreground/55">
              {unmatchedCount} unmatched file
              {unmatchedCount === 1 ? "" : "s"} from the last scan.
            </p>
          )}
        </div>
        <Button
          type="button"
          onClick={onScanNow}
          loading={scanning || running}
          disabled={running}
        >
          Scan now
        </Button>
      </div>
    </div>
  );
}

function UnmatchedSection({ count }: { count: number }) {
  const [expanded, setExpanded] = useState(false);
  const [claiming, setClaiming] = useState<TvUnmatchedFile | null>(null);
  const { data, isLoading } = useQuery({
    queryKey: ["tv", "scan", "unmatched"],
    queryFn: () => tvApi.scan.unmatched(200),
    enabled: expanded && count > 0,
  });

  if (count === 0) return null;

  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="eyebrow">Unmatched files</h2>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => setExpanded((v) => !v)}
        >
          {expanded ? "Hide" : "Show"}
        </Button>
      </div>
      <p className="text-xs leading-relaxed text-foreground/55">
        Files the scanner couldn't link to a monitored episode. Click Identify
        to assign one manually, or check that the filename includes a
        recognizable SxxEyy token + series name for the auto-matcher.
      </p>
      {expanded &&
        (isLoading ? (
          <Skeleton.Rows count={4} />
        ) : !data || data.length === 0 ? (
          <p className="text-xs text-foreground/55">None.</p>
        ) : (
          <div className="overflow-x-auto rounded-md border border-line bg-surface/30">
            <table className="w-full text-xs">
              <thead className="bg-surface-2/50">
                <tr className="text-left">
                  <th className="px-3 py-2 font-mono text-[10px] font-medium uppercase tracking-[0.12em] text-foreground/55">
                    Path
                  </th>
                  <th className="px-3 py-2 font-mono text-[10px] font-medium uppercase tracking-[0.12em] text-foreground/55">
                    Reason
                  </th>
                  <th className="w-24 px-3 py-2"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line/60">
                {data.map((u) => (
                  <tr
                    key={u.id}
                    className="transition hover:bg-surface-2/40"
                  >
                    <td
                      className="max-w-md truncate px-3 py-2 font-mono text-[11px]"
                      title={u.path}
                    >
                      {u.path}
                    </td>
                    <td className="px-3 py-2 text-foreground/65">{u.reason}</td>
                    <td className="px-3 py-2 text-right">
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        onClick={() => setClaiming(u)}
                      >
                        Identify
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      {claiming && (
        <ClaimModal file={claiming} onClose={() => setClaiming(null)} />
      )}
    </section>
  );
}

function ClaimModal({
  file,
  onClose,
}: {
  file: TvUnmatchedFile;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [seriesId, setSeriesId] = useState<number | "">("");
  const [episodeId, setEpisodeId] = useState<number | "">("");

  const seriesList = useQuery({ queryKey: ["series"], queryFn: tvApi.list });

  const sortedSeries = useMemo(
    () =>
      (seriesList.data ?? [])
        .slice()
        .sort((a, b) => a.sort_title.localeCompare(b.sort_title)),
    [seriesList.data],
  );

  const episodes = useQuery({
    queryKey: ["series", seriesId, "episodes"],
    queryFn: () =>
      typeof seriesId === "number"
        ? tvApi.episodes(seriesId)
        : Promise.resolve([]),
    enabled: typeof seriesId === "number",
  });

  const sortedEpisodes = useMemo(
    () =>
      (episodes.data ?? [])
        .slice()
        .sort((a, b) =>
          a.season_number !== b.season_number
            ? a.season_number - b.season_number
            : a.episode_number - b.episode_number,
        ),
    [episodes.data],
  );

  const claimMut = useMutation({
    mutationFn: () => {
      if (typeof episodeId !== "number") {
        return Promise.reject(new Error("pick an episode"));
      }
      return tvApi.scan.claim(file.id, { episode_id: episodeId });
    },
    onSuccess: () => {
      toast("File claimed", "success");
      qc.invalidateQueries({ queryKey: ["tv", "scan"] });
      qc.invalidateQueries({ queryKey: ["series"] });
      onClose();
    },
    onError: (e) =>
      toast(e instanceof Error ? e.message : "Claim failed", "error"),
  });

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    claimMut.mutate();
  };

  return (
    <Modal
      open
      onClose={onClose}
      eyebrow="Manual import"
      title="Identify file"
      width="lg"
    >
      <form onSubmit={onSubmit} className="space-y-4">
        <div className="rounded-md border border-line bg-surface/40 px-3 py-2 text-xs">
          <div className="text-foreground/45">File</div>
          <div className="mt-0.5 truncate font-mono" title={file.path}>
            {file.path}
          </div>
        </div>

        <Field label="Series">
          <Select
            value={seriesId === "" ? "" : String(seriesId)}
            onChange={(e) => {
              setSeriesId(
                e.target.value === "" ? "" : Number(e.target.value),
              );
              setEpisodeId("");
            }}
            disabled={seriesList.isLoading}
          >
            <option value="">— pick a series —</option>
            {sortedSeries.map((s) => (
              <option key={s.id} value={s.id}>
                {s.title}
              </option>
            ))}
          </Select>
        </Field>

        <Field label="Episode">
          <Select
            value={episodeId === "" ? "" : String(episodeId)}
            onChange={(e) =>
              setEpisodeId(
                e.target.value === "" ? "" : Number(e.target.value),
              )
            }
            disabled={typeof seriesId !== "number" || episodes.isLoading}
          >
            <option value="">— pick an episode —</option>
            {sortedEpisodes.map((ep) => (
              <option key={ep.id} value={ep.id}>
                S{String(ep.season_number).padStart(2, "0")}E
                {String(ep.episode_number).padStart(2, "0")}
                {ep.title ? ` — ${ep.title}` : ""}
                {ep.has_file ? " (already has file)" : ""}
              </option>
            ))}
          </Select>
        </Field>

        <div className="flex justify-end gap-2 border-t border-line pt-3">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            loading={claimMut.isPending}
            disabled={typeof episodeId !== "number"}
          >
            Claim
          </Button>
        </div>
      </form>
    </Modal>
  );
}
